import { useState } from "react";
import { Link } from "react-router-dom";
import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLine, FadeIn, Reveal } from "../components/Reveal";
import { Eyebrow } from "../components/atoms";
import { DirectorsNote } from "../components/DirectorsNote";
import { ResourceList } from "../components/ResourceCard";
import { ARTICLES, IMAGES } from "../data/content";

const CATEGORIES = ["ALL", "BUYING STRATEGY", "SELLING STRATEGY", "PORTFOLIO STRATEGY", "MARKET INSIGHT"];

export default function Insights() {
  const [cat, setCat] = useState("ALL");
  const note = ARTICLES.find((a) => a.directorsNote);
  const list = (cat === "ALL" ? ARTICLES : ARTICLES.filter((a) => a.category === cat)).filter(
    (a) => a.slug !== note?.slug
  );

  return (
    <>
      <Seo
        title="Insights — Evidence-based property decisions | 80 Metrics"
        description="Research and practical insights to help you make clearer decisions about buying, selling and retaining property — written by Matthew Clark."
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }])]}
      />
      <section className="bg-charcoal text-ivory" data-testid="insights-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-14 lg:pb-20">
          <FadeIn delay={0.1}>
            <Eyebrow dark>Insights</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>Evidence-based</MaskedLine>
            <MaskedLine delay={0.37}>
              <span className="italic text-gold">property decisions.</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.8}>
            <p className="mt-8 text-sm lg:text-base leading-relaxed text-ivory/60 max-w-xl">
              Research and practical insights to help you make clearer decisions about buying, selling and retaining property.
            </p>
          </FadeIn>
        </div>
      </section>

      {note && (
        <section className="bg-white border-b border-charcoal/10" data-testid="directors-note-featured">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
            <Reveal>
              <Link to={`/insights/${note.slug}`} className="group block max-w-3xl" data-testid="directors-note-card">
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-golddark">Director’s Note</p>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-charcoal transition-colors duration-300 group-hover:text-golddark">
                  {note.title}
                </h2>
                <p className="mt-3 text-xs text-charcoal/45">Matthew Clark · {note.read}</p>
                <p className="mt-4 text-sm lg:text-base leading-relaxed text-charcoal/65 max-w-2xl">{note.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-charcoal group-hover:text-golddark transition-colors duration-300">
                  Read the note
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">→</span>
                </span>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-white" data-testid="insights-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="flex flex-wrap gap-3" data-testid="insights-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase border transition-[background-color,color,border-color] duration-300 ${
                  cat === c
                    ? "bg-charcoal text-gold border-charcoal"
                    : "border-charcoal/25 text-charcoal/60 hover:border-charcoal hover:text-charcoal"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16">
            {list.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06}>
                <Link to={`/insights/${a.slug}`} className="group block" data-testid={`article-card-${a.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-bone">
                    <img
                      src={IMAGES[a.image].src}
                      alt={IMAGES[a.image].alt}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale-[35%] transition-[transform,filter] duration-700 group-hover:scale-[1.03] group-hover:grayscale-0"
                    />
                  </div>
                  <p className="mt-6 text-[11px] font-semibold tracking-[0.28em] uppercase text-golddark">{a.category}</p>
                  <h2 className="mt-3 font-serif text-2xl lg:text-3xl font-medium leading-snug text-charcoal transition-colors duration-300 group-hover:text-golddark">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{a.excerpt}</p>
                  <p className="mt-4 text-xs text-charcoal/45">
                    {a.date} · {a.read} · Matthew Clark
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="insights-guides">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 max-w-3xl">
          <ResourceList service="insights" />
        </div>
      </section>

      <section className="bg-bone" data-testid="insights-directors-note">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 max-w-3xl">
          <DirectorsNote dark={false} />
        </div>
      </section>
    </>
  );
}
